import Miner20 from '../../../assets/img/miner_20.png';
import BackgroundGridImg from '../../../assets/img/bg-game-2.jpeg';
import { miners } from '../../../utils/constants';

import React, { useRef, useEffect, useState } from 'react';
import { canvasHeight, canvasWidth, roomDrawParams, RoomProperties, startTileOffsets, tileHeightByRoom, tileWidthByRoom } from '../../../utils/constants';

interface RoomCanvasProps {
    facilityIndex: number; // 1, 2, 3, 4, 5, or 6
    minerPositions?: { [key: string]: any };
    onTileSelect?: (x: number, y: number) => void;
    gridEnabled: boolean; // Optional prop to enable/disable grid
}

const RoomCanvas: React.FC<RoomCanvasProps> = ({
    facilityIndex = 1,
    minerPositions = {},
    onTileSelect,
    gridEnabled
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [selectedTile, setSelectedTile] = useState<{ x: number; y: number } | null>(null);
    const roomImage = useRef<HTMLImageElement>(new Image());
    const minerImage = useRef<HTMLImageElement>(new Image());
    const backgroundImage = useRef<HTMLImageElement>(new Image());
    const containerRef = useRef<HTMLDivElement>(null);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef<{ x: number; y: number } | null>(null);
    const panStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const { x: startTileX, y: startTileY } = startTileOffsets[facilityIndex] || { x: 280, y: 325 };

    const tileWidth = tileWidthByRoom[facilityIndex] || 180;
    const tileHeight = tileHeightByRoom[facilityIndex] || 90;

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        console.log('Setting up image sources');
        backgroundImage.current.src = BackgroundGridImg;
        console.log('Background image source set to:', facilityIndex);
        roomImage.current.src = RoomProperties[facilityIndex].img;
        // minerImage.current.src = Miner20;
        // console.log('Miner image source set to:', Miner20);

        const loadImages = () => {
            let loadedImages = 0;
            const totalImages = 2;

            const checkAllLoaded = () => {
                loadedImages++;
                if (loadedImages === 1) {
                    console.log('Room image loaded successfully');
                } else {
                    console.log('Miner image loaded successfully');
                    console.log('Miner image size:', minerImage.current.width, 'x', minerImage.current.height);
                }

                if (loadedImages === totalImages) {
                    console.log('All images loaded, drawing canvas');
                    draw(ctx);
                }
            };

            roomImage.current.onload = checkAllLoaded;
            minerImage.current.onload = checkAllLoaded;
            backgroundImage.current.onload = checkAllLoaded;

            // Add error handlers with more detail
            roomImage.current.onerror = (e) => console.error('Failed to load room image:', e);
            minerImage.current.onerror = (e) => console.error('Failed to load miner image:', e);
            backgroundImage.current.onerror = (e) => console.error('Failed to load miner image:', e);
        };

        loadImages();
    }, [facilityIndex]);

    useEffect(() => {
        let animationFrameId: number;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const animate = () => {
            ctx.imageSmoothingEnabled = false;
            draw(ctx);
            animationFrameId = requestAnimationFrame(animate);
        };

        animationFrameId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [minerPositions, selectedTile, gridEnabled, facilityIndex]);

    const draw = (ctx: CanvasRenderingContext2D, selectedTileOverride?: { x: number; y: number }) => {
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        const { x, y, w, h } = roomDrawParams[facilityIndex] || roomDrawParams[0];
        ctx.drawImage(roomImage.current, x, y, w, h);

        const selTile = selectedTileOverride || selectedTile;
        for (let x = 0; x < RoomProperties[facilityIndex].x; x++) {
            for (let y = 0; y < RoomProperties[facilityIndex].y; y++) {
                const posX = startTileX + (y - x) * tileWidth / 2;
                const posY = startTileY + (x + y) * tileHeight / 2;
                if (gridEnabled) {
                    if (selTile && selTile.x === x && selTile.y === y) {
                        drawTile(ctx, posX, posY, '#FFD700', true);
                    } else {
                        drawTile(ctx, posX, posY, 'rgba(255, 255, 255, 1)', false);
                    }
                }
            }
        }
    };

    const drawPixelLine = (
        ctx: CanvasRenderingContext2D,
        x0: number, y0: number,
        x1: number, y1: number,
        color: string
    ) => {
        ctx.fillStyle = color;
        // Algoritmo de Bresenham para linhas pixeladas
        let dx = Math.abs(x1 - x0), sx = x0 < x1 ? 1 : -1;
        let dy = -Math.abs(y1 - y0), sy = y0 < y1 ? 1 : -1;
        let err = dx + dy, e2;
        while (true) {
            ctx.fillRect(x0, y0, 2, 2);
            if (x0 === x1 && y0 === y1) break;
            e2 = 2 * err;
            if (e2 >= dy) { err += dy; x0 += sx; }
            if (e2 <= dx) { err += dx; y0 += sy; }
        }
    };

    const drawTile = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string, shadow: boolean = false) => {
        ctx.save();
        if (shadow) {
            ctx.shadowColor = 'yellow';
            ctx.shadowBlur = 10;
        }
        // Cálculo dos 4 vértices do losango
        const p0 = [Math.round(x), Math.round(y)];
        const p1 = [Math.round(x + tileWidth / 2), Math.round(y + tileHeight / 2)];
        const p2 = [Math.round(x), Math.round(y + tileHeight)];
        const p3 = [Math.round(x - tileWidth / 2), Math.round(y + tileHeight / 2)];
        // Desenhar as 4 linhas do losango com pixels
        drawPixelLine(ctx, p0[0], p0[1], p1[0], p1[1], color);
        drawPixelLine(ctx, p1[0], p1[1], p2[0], p2[1], color);
        drawPixelLine(ctx, p2[0], p2[1], p3[0], p3[1], color);
        drawPixelLine(ctx, p3[0], p3[1], p0[0], p0[1], color);
        ctx.restore();
    };

    const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        // const mouseX = e.clientX - rect.left;
        // const mouseY = e.clientY - rect.top;
        const mouseX = (e.clientX - rect.left) / scale;
        const mouseY = (e.clientY - rect.top) / scale;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        for (let x = 0; x < RoomProperties[facilityIndex].x; x++) {
            for (let y = 0; y < RoomProperties[facilityIndex].y; y++) {
                const posX = startTileX + (y - x) * tileWidth / 2;
                const posY = startTileY + (x + y) * tileHeight / 2;

                const path = new Path2D();
                path.moveTo(posX, posY);
                path.lineTo(posX + tileWidth / 2, posY + tileHeight / 2);
                path.lineTo(posX, posY + tileHeight);
                path.lineTo(posX - tileWidth / 2, posY + tileHeight / 2);
                path.closePath();

                if (ctx.isPointInPath(path, mouseX, mouseY)) {
                    const isAlreadySelected = selectedTile?.x === x && selectedTile?.y === y;

                    if (isAlreadySelected) {
                        setSelectedTile(null); // Deselect tile
                        draw(ctx, undefined);
                        if (onTileSelect) onTileSelect(x, y); // Optional: signal deselection
                    } else {
                        setSelectedTile({ x, y });
                        draw(ctx, { x, y });
                        if (onTileSelect) onTileSelect(x, y);
                    }

                    return;
                }
            }
        }
    };

    const [scale, setScale] = useState(1);

    useEffect(() => {
        const handleResize = () => {
            const newScale = Math.min(1, window.innerWidth / canvasWidth);
            setScale(newScale);
        };

        handleResize(); // Set on mount
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        setDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY };
        panStart.current = { ...pan };
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!dragging || !dragStart.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPan({
            x: panStart.current.x + dx,
            y: panStart.current.y + dy,
        });
    };

    const handleMouseUp = () => {
        setDragging(false);
        dragStart.current = null;
    };

    useEffect(() => {
        if (dragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging]);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: `${canvasHeight}px`,
                overflow: 'hidden',
            }}
        >
            {/* Fixed background grid */}
            <img
                src={BackgroundGridImg}
                alt="background grid"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${canvasWidth}px`,
                    height: `${canvasHeight}px`,
                    zIndex: 0,
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}
                draggable={false}
            />

            {/* Draggable content */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                    transformOrigin: 'top left',
                    width: `${canvasWidth}px`,
                    height: `${canvasHeight}px`,
                    zIndex: 1,
                    cursor: dragging ? 'grabbing' : 'grab',
                }}
                onMouseDown={handleMouseDown}
            >
                <canvas
                    ref={canvasRef}
                    width={canvasWidth}
                    height={canvasHeight}
                    style={{
                        background: 'transparent',
                        imageRendering: 'pixelated',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1
                    }}
                    onClick={handleCanvasClick}
                />
                {Object.entries(minerPositions)
                    .sort(([keyA], [keyB]) => {
                        const [xA, yA] = keyA.split(',').map(Number);
                        const [xB, yB] = keyB.split(',').map(Number);
                        return (xA + yA) - (xB + yB);
                    })
                    .map(([key, minerData]) => {
                        const [x, y] = key.split(',').map(Number);
                        const minerObj = miners.find(m => m.id === minerData.minerIndex);
                        if (!minerObj) return null;

                        const posX = startTileX + (y - x) * tileWidth / 2;
                        const posY = startTileY + (x + y) * tileHeight / 2;

                        const scaleH = 1.7;
                        const scaleW = 1.1;
                        const imgWidth = tileWidth * scaleW;
                        const imgHeight = tileHeight * scaleH;
                        const groundX = posX;
                        const groundY = posY + tileHeight;
                        const isFlipped = (x + y) % 2 === 1;
                        const footOffset = 1.0;

                        return (
                            <img
                                key={key}
                                src={minerObj.gif}
                                alt={minerObj.name}
                                style={{
                                    position: 'absolute',
                                    left: groundX - imgWidth / 2,
                                    top: groundY - imgHeight * footOffset,
                                    width: imgWidth,
                                    height: imgHeight,
                                    pointerEvents: 'none',
                                    imageRendering: 'pixelated',
                                    transform: isFlipped ? 'scaleX(-1)' : 'none',
                                    zIndex: 2
                                }}
                            />
                        );
                    })}
            </div>
        </div>
    );
};

export default RoomCanvas;
